<template>
  <div class="bview">
    <section
      v-for="col in desk.columns"
      :key="col.id"
      class="bcol"
      :style="{ '--cc': col.color || '#9aa4b2' }"
    >
      <header class="bcol__head">
        <span class="bcol__dot" />
        <span class="bcol__name">{{ col.name }}</span>
        <span class="bcol__count">{{ col.total || 0 }}</span>
        <v-spacer />
        <button
          type="button"
          class="bcol__add"
          :title="$t('task')"
          @click="desk.openCreateTask('task', null, col, desk.template)"
        >
          <v-icon small>mdi-plus</v-icon>
        </button>
      </header>

      <div class="bcol__scroll">
        <draggable
          v-model="col.list"
          class="bcol__list"
          group="taskDesk"
          :animation="200"
          ghost-class="bcol__ghost"
          drag-class="bcol__drag"
          filter=".tcard__actions"
          :prevent-on-filter="false"
          @change="desk.change(col.id, $event)"
        >
          <div
            v-for="element in col.list"
            :key="element.ref && element.ref.id ? element.ref.id : 'empty-' + col.id"
            class="bcol__item"
          >
            <div v-if="!(element.id > 0)" class="bcol__empty">
              <v-icon small>mdi-tray-arrow-down</v-icon>
              {{ $t('taskDeskDropHere') }}
            </div>
            <task-card
              v-else
              :el="element"
              :color="col.color"
              :done="desk.isDoneCol(col)"
            />
          </div>
        </draggable>
        <div
          v-if="desk.hasMore(col)"
          v-observe-visibility="{ callback: (v) => v && desk.getTask(col) }"
          class="bcol__more"
        >
          <v-progress-circular :size="18" :width="2" color="primary" indeterminate />
        </div>
      </div>

      <footer class="bcol__foot">
        <template v-if="col.descVisibility">
          <textarea
            ref="quick"
            v-model="col.description"
            v-focus
            class="bcol__quick"
            rows="2"
            :placeholder="$t('enter-title')"
            @keydown.enter="desk.submitTaskPreview(col, $event)"
            @keydown.esc="col.descVisibility = false"
          />
          <div class="bcol__quick-actions">
            <v-btn
              color="primary"
              depressed
              small
              height="30"
              :loading="desk.isLoading"
              @click="desk.submitTaskPreview(col, $event)"
            >
              {{ $t('add-task') }}
            </v-btn>
            <button type="button" class="bcol__x" @click="col.descVisibility = false">
              <v-icon small>mdi-close</v-icon>
            </button>
          </div>
        </template>
        <button v-else type="button" class="bcol__new" @click="desk.openDescColumn(col)">
          <v-icon small>mdi-plus</v-icon>
          {{ $t('add-task') }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import TaskCard from './TaskCard'

export default {
  name: 'BoardView',
  components: { draggable, TaskCard },
  directives: {
    focus: { inserted: el => el.focus() },
  },
  inject: ['desk'],
}
</script>

<style scoped>
.bview {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: stretch;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 6px;
}
.bview::-webkit-scrollbar {
  height: 8px;
}
.bview::-webkit-scrollbar-thumb {
  background: #cfd5de;
  border-radius: 4px;
}
.bcol {
  flex: 0 0 300px;
  width: 300px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #e9ecf2;
  border-radius: 8px;
}
.theme--dark .bcol {
  background: #1a1a1a;
}
.bcol__head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-top: 3px solid var(--cc);
  border-radius: 8px 8px 0 0;
}
.bcol__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--cc);
}
.bcol__name {
  font-weight: 700;
  font-size: 0.88rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bcol__count {
  font-size: 0.72rem;
  min-width: 22px;
  height: 20px;
  line-height: 20px;
  padding: 0 6px;
  text-align: center;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.07);
  color: #4b5563;
}
.theme--dark .bcol__count {
  background: rgba(255, 255, 255, 0.1);
  color: #ccc;
}
.bcol__add,
.bcol__x {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.bcol__add:hover,
.bcol__x:hover {
  background: rgba(0, 0, 0, 0.07);
}
.bcol__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 8px;
}
.bcol__scroll::-webkit-scrollbar {
  width: 6px;
}
.bcol__scroll::-webkit-scrollbar-thumb {
  background: #c9d0da;
  border-radius: 3px;
}
.bcol__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 60px;
  padding-bottom: 8px;
}
.bcol__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 64px;
  border: 1.5px dashed #c5ccd6;
  border-radius: 8px;
  font-size: 0.78rem;
  color: #8a94a6;
}
.bcol__empty .v-icon {
  color: inherit;
}
.bcol__ghost {
  opacity: 0.4;
}
.bcol__ghost ::v-deep .tcard {
  border-style: dashed;
  border-color: var(--v-primary-base);
}
.bcol__more {
  display: flex;
  justify-content: center;
  padding: 6px 0 10px;
}
.bcol__foot {
  flex: 0 0 auto;
  padding: 8px;
}
.bcol__new {
  width: 100%;
  height: 34px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 0.82rem;
  color: #5b6475;
}
.bcol__new .v-icon {
  color: inherit;
}
.bcol__new:hover {
  background: rgba(0, 0, 0, 0.06);
  color: var(--v-primary-base);
}
.bcol__quick {
  width: 100%;
  resize: none;
  background: #fff;
  border: 1px solid var(--v-primary-base);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.85rem;
  outline: none;
  max-height: 140px;
}
.theme--dark .bcol__quick {
  background: #1e1e1e;
  color: #eee;
}
.bcol__quick-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}
</style>
